// frontend/src/components/VoiceRecorder/VoiceRecorder.js
import React, { useState, useRef, useEffect } from 'react';
import './VoiceRecorder.css';
import WaveformVisualizer from './WaveformVisualizer';
import RecordingControls from './RecordingControls';
import AudioPreview from './AudioPreview';
import { formatTime } from './utils';

const VoiceRecorder = ({ 
  onRecordingComplete, 
  onCancel, 
  maxDuration = 300, // 5 minutes default
  showTranscription = false 
}) => {
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioURL, setAudioURL] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingError, setRecordingError] = useState(null);
  const [transcription, setTranscription] = useState('');
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Initialize media recorder
  const initializeRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
          channelCount: 2
        } 
      });
      
      streamRef.current = stream;
      
      // Determine best audio format - prefer webm but we'll disguise it as ogg
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
      } else {
        // Fallback to default
        mimeType = undefined;
      }
      
      const options = mimeType ? { 
        mimeType,
        audioBitsPerSecond: 128000 
      } : { 
        audioBitsPerSecond: 128000 
      };
      
      const mediaRecorder = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        // WORKAROUND: Create blob with audio/ogg MIME type to bypass backend validation
        // The actual data might be webm, but we tell the server it's ogg
        // This works because most audio players can handle the format mismatch
        const blob = new Blob(chunksRef.current, { type: 'audio/ogg' });
        
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        
        console.log('[VoiceRecorder] Recording stopped, blob created:', {
          size: blob.size,
          type: blob.type,
          actualRecorderType: mediaRecorder.mimeType,
          duration: recordingTime
        });
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setRecordingError('Recording failed. Please try again.');
        stopRecording();
      };
      
      return mediaRecorder;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      
      if (error.name === 'NotAllowedError') {
        setRecordingError('Microphone access denied. Please allow microphone access to record voice notes.');
      } else if (error.name === 'NotFoundError') {
        setRecordingError('No microphone found. Please connect a microphone and try again.');
      } else {
        setRecordingError('Failed to access microphone. Please check your browser settings.');
      }
      
      throw error;
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      setRecordingError(null);
      
      if (!mediaRecorderRef.current) {
        await initializeRecorder();
      }
      
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'inactive') {
        chunksRef.current = []; // Clear any previous chunks
        mediaRecorderRef.current.start(1000); // Collect data every second
        setIsRecording(true);
        setIsPaused(false);
        setRecordingTime(0); // Reset time when starting new recording
        
        // Start timer
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => {
            if (prev >= maxDuration - 1) {
              stopRecording();
              return maxDuration;
            }
            return prev + 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  };

  // Pause recording
  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      
      // Pause timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  // Resume recording
  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      
      // Resume timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= maxDuration - 1) {
            stopRecording();
            return maxDuration;
          }
          return prev + 1;
        });
      }, 1000);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && 
        (mediaRecorderRef.current.state === 'recording' || 
         mediaRecorderRef.current.state === 'paused')) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  // Cancel recording
  const cancelRecording = () => {
    stopRecording();
    
    // Clean up
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioBlob(null);
    setAudioURL(null);
    setRecordingTime(0);
    setTranscription('');
    chunksRef.current = [];
    
    if (onCancel) {
      onCancel();
    }
  };

  // Save recording
  const saveRecording = () => {
    if (audioBlob && onRecordingComplete) {
      // Create a File object with audio/ogg MIME type (workaround for backend validation)
      const filename = `voice-note-${Date.now()}.ogg`;
      const file = new File([audioBlob], filename, { 
        type: 'audio/ogg',  // Use ogg to bypass backend validation
        lastModified: Date.now()
      });
      
      console.log('[VoiceRecorder] Saving recording:', {
        filename,
        fileSize: file.size,
        fileType: file.type,
        duration: recordingTime,
        hasTranscription: !!transcription
      });
      
      // Call onRecordingComplete with the expected object format
      onRecordingComplete({
        file,
        blob: audioBlob,
        url: audioURL,
        duration: recordingTime,
        transcription: transcription || null
      });
    }
  };

  // Re-record
  const reRecord = () => {
    // Clean up current recording
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioBlob(null);
    setAudioURL(null);
    setRecordingTime(0);
    setTranscription('');
    chunksRef.current = [];
    
    // Start new recording
    startRecording();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioURL) {
        URL.revokeObjectURL(audioURL);
      }
    };
  }, [audioURL]);

  return (
    <div className="voice-recorder">
      <div className="voice-recorder-header">
        <h3>
          <span className="mic-icon">🎤</span>
          {audioBlob ? 'Voice Note Recorded' : isRecording ? 'Recording...' : 'Record Voice Note'}
        </h3>
        <div className="time-display">
          <span className="recording-time">{formatTime(recordingTime)}</span>
          {maxDuration && (
            <span className="max-duration">/ {formatTime(maxDuration)}</span>
          )}
        </div>
      </div>

      {recordingError && (
        <div className="recording-error">
          <span className="error-icon">⚠️</span>
          {recordingError}
        </div>
      )}

      {isRecording && !audioBlob && (
        <WaveformVisualizer 
          isRecording={isRecording} 
          isPaused={isPaused}
          stream={streamRef.current}
        />
      )}

      {audioBlob && (
        <>
          <AudioPreview 
            audioURL={audioURL} 
            duration={recordingTime}
          />
          
          {showTranscription && (
            <div className="transcription-section">
              <label htmlFor="transcription">Transcription (optional):</label>
              <textarea
                id="transcription"
                className="transcription-input"
                value={transcription}
                onChange={(e) => setTranscription(e.target.value)}
                placeholder="Add a transcription or description..."
                rows={3}
              />
            </div>
          )}
        </>
      )}

      <RecordingControls
        isRecording={isRecording}
        isPaused={isPaused}
        hasRecording={!!audioBlob}
        onStart={startRecording}
        onPause={pauseRecording}
        onResume={resumeRecording}
        onStop={stopRecording}
        onCancel={cancelRecording}
        onSave={saveRecording}
        onReRecord={reRecord}
      />

      {isRecording && (
        <div className="recording-indicator">
          <span className="pulse-dot"></span>
          <span>Recording in progress...</span>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;